/**
 * SieFileStagingView - Staging view for multiple SIE files before merge
 * 
 * Displays selected files with ability to:
 * - View file details (name, size)
 * - Remove individual files
 * - Clear all files
 * - Trigger merge processing
 */

import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  FileSpreadsheet, 
  X, 
  Trash2, 
  Loader2, 
  CheckCircle, 
  AlertTriangle,
  Upload,
  Merge,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface StagedFile {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'processing' | 'done' | 'error';
  progress: number;
  error?: string;
}

interface SieFileStagingViewProps {
  files: StagedFile[];
  onRemoveFile: (id: string) => void;
  onClearAll: () => void;
  onAddMore: () => void;
  onProcess: () => void;
  isProcessing: boolean;
  maxFiles?: number;
  className?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getEstimatedProcessingTime(totalBytes: number, fileCount: number): string {
  // Rough estimate: ~0.5s per 100KB + 1s per file for parsing
  const seconds = Math.ceil((totalBytes / 100000) * 0.5 + fileCount * 1);
  if (seconds < 60) return `~${seconds} sek`;
  return `~${Math.ceil(seconds / 60)} min`;
}

export function SieFileStagingView({
  files,
  onRemoveFile,
  onClearAll,
  onAddMore,
  onProcess,
  isProcessing,
  maxFiles = 50,
  className,
}: SieFileStagingViewProps) {
  const totalSize = useMemo(() => 
    files.reduce((sum, f) => sum + f.file.size, 0),
    [files]
  );

  const estimatedTime = useMemo(() => 
    getEstimatedProcessingTime(totalSize, files.length),
    [totalSize, files.length]
  );

  const overallProgress = useMemo(() => {
    if (files.length === 0) return 0;
    const totalProgress = files.reduce((sum, f) => sum + f.progress, 0);
    return Math.round(totalProgress / files.length);
  }, [files]);

  const hasErrors = files.some(f => f.status === 'error');
  const allDone = files.every(f => f.status === 'done');
  const canAddMore = files.length < maxFiles;

  return (
    <Card className={cn('', className)}>
      <CardContent className="pt-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-muted-foreground" />
            <span className="font-medium">
              Valda filer ({files.length} st)
            </span>
            {files.length >= maxFiles && (
              <Badge variant="secondary" className="text-xs">Max uppnått</Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            disabled={isProcessing || files.length === 0}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Rensa alla
          </Button>
        </div>

        {/* File list */}
        <ScrollArea className="h-[240px] pr-4 mb-4">
          <div className="space-y-2">
            {files.map((stagedFile) => (
              <div
                key={stagedFile.id}
                className={cn(
                  'flex items-center justify-between p-3 rounded-lg border bg-card',
                  stagedFile.status === 'error' && 'border-destructive bg-destructive/5',
                  stagedFile.status === 'done' && 'border-green-500/50 bg-green-50/50 dark:bg-green-950/20',
                )}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Status icon */}
                  {stagedFile.status === 'pending' && (
                    <FileSpreadsheet className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  )}
                  {(stagedFile.status === 'uploading' || stagedFile.status === 'processing') && (
                    <Loader2 className="h-4 w-4 animate-spin text-primary flex-shrink-0" />
                  )}
                  {stagedFile.status === 'done' && (
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  )}
                  {stagedFile.status === 'error' && (
                    <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0" />
                  )}

                  {/* File info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {stagedFile.file.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(stagedFile.file.size)}
                      {stagedFile.error && (
                        <span className="text-destructive ml-2">{stagedFile.error}</span>
                      )}
                    </p>
                  </div>

                  {/* Progress bar for uploading/processing */}
                  {(stagedFile.status === 'uploading' || stagedFile.status === 'processing') && (
                    <div className="w-20">
                      <Progress value={stagedFile.progress} className="h-1" />
                    </div>
                  )}
                </div>

                {/* Remove button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onRemoveFile(stagedFile.id)}
                  disabled={isProcessing}
                  className="h-8 w-8 text-muted-foreground hover:text-destructive flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </ScrollArea>

        {/* Summary */}
        <div className="flex items-center justify-between text-sm text-muted-foreground mb-4 px-1">
          <span>Totalt: {formatFileSize(totalSize)}</span>
          <span>Beräknad tid: {estimatedTime}</span>
        </div>

        {/* Overall progress during processing */}
        {isProcessing && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">Bearbetar filer...</span>
              <span className="font-medium">{overallProgress}%</span>
            </div>
            <Progress value={overallProgress} className="h-2" />
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="outline"
            onClick={onAddMore}
            disabled={isProcessing || !canAddMore}
          >
            <Upload className="h-4 w-4 mr-2" />
            Lägg till fler filer
          </Button>
          
          <Button
            onClick={onProcess}
            disabled={isProcessing || files.length === 0 || hasErrors}
            className="min-w-[180px]"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Bearbetar...
              </>
            ) : (
              <>
                <Merge className="h-4 w-4 mr-2" />
                Slå samman och bearbeta
              </>
            )}
          </Button>
        </div>

        {/* Warning for large uploads */}
        {totalSize > 20 * 1024 * 1024 && (
          <div className="mt-4 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Stor datamängd ({formatFileSize(totalSize)}). Bearbetningen kan ta längre tid.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
